import { addToCart, redirectToCart } from 'features/cart';
import { showAddToCartFailureModal } from 'features/cart/desktop';
import { addToWishList, showAddToWishListError } from 'features/wishList/desktop';
import { dataCaptureTimeout } from 'ui/config';

qtee.customDesign = window.qtee.customDesign || {};

qtee.customDesign.utils = {};

/**
 * Add to cart
 * @param {String} sku
 * @param {Integer} qty
 * @param {String} token
 * @param {String} color
 * @param {Array<Promise>} allPromises - Optional promises to wait for before redirect
 */
qtee.customDesign.utils.addToCart = (data, allPromises = []) => {

	const successCallback = () => {

		if (!window.lp.globals.enableDataCapture) {

			redirectToCart();
			return;

		}

		const timeout = window.setTimeout(redirectToCart, dataCaptureTimeout);
		Promise.all(allPromises).finally(() => {

			window.clearTimeout(timeout);
			redirectToCart();

		});

	};

	addToCart({
		arrData: data,
		successCallback,
		errorCallback: (errors, xhr, response) => {

			showAddToCartFailureModal({
				cartStatus: errors,
				response,
				fromModal: true
			});

		},
		includeCartOverview: false,
		rememberLastAddedItem: true
	});

};

/**
 * Add to wishlist
 * @param {String} sku
 * @param {Integer} qty
 * @param {String} token
 */
qtee.customDesign.utils.addToWishlist = (sku, qty, token, color, skip) => {

	const data = [
		{
			ShortSku: sku,
			Quantity: qty,
			CustomizedProductToken: token,
			Pattern: { Trim: color }
		}
	];
	const request = addToWishList(
		data,
		() => {

			if (!skip) {

				window.location.href = '/wish-list';

			}

		},
		(maxItemCountReached) => {

			showAddToWishListError(maxItemCountReached, true);

		},
		false,
		true
	);

};

/**
 * Add customized product model to cart
 * @param {Backbone.model}   model  customized product
 * @param {Integer} qty
 * @param {Callback} done
 * @param {Callback} fail
 * @param {Callback} always
 * @return {jqXHR}
 */
qtee.customDesign.utils.addModelToCart = (model, qty, done, fail, always) => {

	var xhr;

	// if model is not updated on server then create clone on server and use token from this new model
	if (Backbone.customDesign.isEmulateOn()) {

		const clonedModel = this.customProdModel.clone();

		Backbone.customDesign.stopEmulateAjax();
		var xhr = clonedModel.save({}, { cloneBySprites: true });
		Backbone.customDesign.startEmulateAjax();

		xhr = xhr.then(() => qtee.customDesign.utils.addToCart(sku, qty, clonedModel.get('Token')));

	} else {

		xhr = qtee.customDesign.utils.addToCart(model.get('ShortSku'), qty, model.get('Token'));

	}

	return xhr.done(done).fail(fail).always(always);

};

/**
 * Add to favorites
 * @param {String} sku
 * @param {String} token
 */
qtee.customDesign.utils.addToFavorites = (sku, token) => $.ajax({
	url: '/api/favorite/items',
	method: 'post',
	data: { ShortSku: sku, CustomizedProductToken: token }
});

/**
 * Add customized product model to favorites.
 * @param {Backbone.Model} model customized product model
 * @param {Callback} done
 * @param {Callback} fail
 * @param {Callback} always
 * @return {jqXHR}
 */
qtee.customDesign.utils.addModelToFavorites = (model, done, fail, always) => {

	let xhr;

	// if model is not updated on server then create clone on server and use token from this new model
	if (Backbone.customDesign.isEmulateOn()) {

		const clonedModel = model.clone();

		Backbone.customDesign.stopEmulateAjax();
		xhr = clonedModel.save({}, { cloneBySprites: true });
		Backbone.customDesign.startEmulateAjax();

		xhr = xhr.then(() => qtee.customDesign.utils.addToFavorites(clonedModel.get('ShortSku'), clonedModel.get('Token')));

	} else {

		xhr = qtee.customDesign.utils.addToFavorites(model.get('ShortSku'), model.get('Token'));

	}

	return xhr.done(done).fail(fail).always(always);

};

qtee.customDesign.utils.generateProductQueryString = (attributes) => {

	const params = [];

	function addParam (key, value) {

		if (value != undefined) {

			params.push(`${key}=${encodeURIComponent(value).replace(/'/g, '%27')}`);

		} else {

			console.warn(`Undefined value for key "${key}"`);

		}

	}

	if (attributes.customizedProductToken) {

		// customized product
		addParam('shortSku', attributes.ShortSku);
		addParam('customizedProductToken', attributes.customizedProductToken);
		addParam('name', attributes.Name);

	} else {

		// regular product
		addParam('shortSku', attributes.ShortSku);
		addParam('patternId', attributes.SelectedColorCombination.PatternId);
		addParam('trim', attributes.SelectedColorCombination.Trim);
		addParam('layerColors', attributes.SelectedColorCombination.LayerColors.join(','));
		addParam('name', attributes.Name);

	}

	return params.join('&');

};

qtee.customDesign.utils.getSkuInfo = (shortSku, availableProducts) => {

	for (let i = 0; i < availableProducts.length; i++) {

		for (let ii = 0; ii < availableProducts[i].AvailableSizes.length; ii++) {

			if (shortSku == availableProducts[i].AvailableSizes[ii].ShortSku) {

				return {
					colorHex: availableProducts[i].ColorHex,
					colorHex2: availableProducts[i].ColorHex2,
					groupSku: availableProducts[i].GroupSku,
					shortSku: availableProducts[i].AvailableSizes[ii].ShortSku,
					isSelected: availableProducts[i].AvailableSizes[ii].IsSelected,
					isNetAvailable: availableProducts[i].AvailableSizes[ii].IsNetAvailable
				};

			}

		}

	}
	return undefined;

};

qtee.customDesign.utils.calcDuplicatePosition = (type, startX, startY, rotate) => {

	const offset = 50;
	const posThreshold = 10;
	const rotThreshold = 5.4;
	const elements = qtee.customDesign.canvas.getAllElementsByType(type);
	let x = startX - offset;
	let y = startY + offset;
	const search = function () {

		let found = false;

		x += offset;
		y -= offset;

		elements.forEach((el) => {

			const obj = el.rel.freeTransform.attrs;

			if (
				obj.translate.x + obj.center.x <= x + posThreshold
				&& obj.translate.x + obj.center.x >= x - posThreshold
				&& obj.translate.y + obj.center.y <= y + posThreshold
				&& obj.translate.y + obj.center.y >= y - posThreshold
				&& obj.rotate <= rotate + rotThreshold
				&& obj.rotate >= rotate - rotThreshold
			) {

				found = true;

			}

		});

		// if a sprite is located in same position, then repeat search again with new coordinates
		if (found) {

			search();

		}

	};

	search();

	return { x, y };

};

qtee.customDesign.utils.modelImgUrlBuilder = (() => {

	/**
	 * Build text's part of url
	 * @private
	 * @param  {Backbone.Model} model text model
	 * @param  {Float} widthFactor
	 * @param  {Float} heightFactor
	 * @return {String}
	 */
	function _buildTextUrlPart (model, widthFactor, heightFactor) {

		let url;
		let layer;
		let rotate;
		let x;
		let y;
		let fontSize;
		let fontName;
		let red;
		let green;
		let blue;
		let text;

		layer = model.get('ZOrder') + 1;
		rotate = model.get('RotationalDegree');
		x = Math.round(model.get('OriginX') * widthFactor);
		y = Math.round(model.get('OriginY') * heightFactor);
		fontName = model.get('FontName');
		red = model.get('Red');
		green = model.get('Green');
		blue = model.get('Blue');
		fontSize = Math.round(2 * model.get('FontSize') * widthFactor);
		text = model.get('Text').replace('\\', '\\\\').replace('{', '\\{').replace('}', '\\}');

		url =			`layer=${layer}&rotate=${rotate}&pos=${x},${y}&anchorN=0,0`
			+ `&textPs={\\fonttbl{\\f1${fontName}}}`
			+ `{\\colortbl;\\red${red}\\green${green}\\blue${blue};}`
			+ `\\fs${fontSize}\\cf1\\f1${model.get('IsBold') ? '\\b1' : ''}${
				model.get('IsItalic') ? '\\i1' : ''
			}{${text}}`;

		return url;

	}

	/**
	 * Build image's part of url
	 * @private
	 * @param  {Backbone.Model} model image model
	 * @param  {Float} widthFactor
	 * @param  {Float} heightFactor
	 * @return {String}
	 */
	function _buildImageUrlPart (model, widthFactor, heightFactor) {

		let url;
		let layer;
		let rotate;
		let scale;
		let x;
		let y;
		let width;
		let height;
		let fileName;
		let re;

		layer = model.get('ZOrder') + 1;
		rotate = model.get('RotationalDegree');
		scale = model.get('Scale');
		x = Math.round(model.get('OriginX') * widthFactor);
		y = Math.round(model.get('OriginY') * heightFactor);
		width = model.get('Width') * scale * widthFactor;
		height = model.get('Height') * scale * heightFactor;

		re = /\(ugc-file:(.*)\)/;

		fileName = model.get('ImageUrl').match(re)[1];

		url =			`layer=${layer}&src=is(ugc-file:${fileName})`
			+ `&rotate=${rotate}&pos=${x},${y}&anchorN=0,0`
			+ `&size=${width},${height}`;

		return url;

	}

	/**
	 * Build design's part of url
	 * @private
	 * @param  {Backbone.Model} model design model
	 * @param  {Float} widthFactor
	 * @param  {Float} heightFactor
	 * @return {String}
	 */
	function _buildDesignUrlPart (model, widthFactor, heightFactor) {

		let url;
		let layer;
		let rotate;
		let scale;
		let x;
		let y;
		let width;
		let height;
		let fullId;
		let re;

		layer = model.get('ZOrder') + 1;
		rotate = model.get('RotationalDegree');
		scale = model.get('Scale');
		x = Math.round(model.get('OriginX') * widthFactor);
		y = Math.round(model.get('OriginY') * heightFactor);
		width = model.get('Width') * widthFactor * scale;
		height = model.get('Height') * heightFactor * scale;

		re = new RegExp(`${model.get('PatternId')}_[\\d]+_w`);
		fullId = model.get('PatternImageUrl').match(re)[0];

		url = `layer=${layer}&src=${fullId}&rotate=${rotate}&pos=${x},${y}&anchorN=0,0` + `&size=${width},${height}`;

		return url;

	}

	/**
	 * Update/set the width and height of url
	 * @param {String} url
	 * @param {Number} width
	 * @param {Number} height
	 */
	function setDimensions (url, width, height) {

		let re;

		// update width
		if (width) {

			re = /(.*)&src=is\((.*)\)(.*)(&wid=[0-9]*)(.*)/i;
			if (url.search(re) >= 0) {

				url = url.replace(re, `$1&src=is($2)$3&wid=${width}$5`);

			} else {

				url += `&wid=${width}`;

			}

		}
		// update height
		if (height) {

			re = /(.*)&src=is\((.*)\)(.*)(&hei=[0-9]*)(.*)/i;
			if (url.search(re) >= 0) {

				url = url.replace(re, `$1&src=is($2)$3&hei=${height}$5`);

			} else {

				url += `&hei=${height}`;

			}

		}
		return url;

	}

	/**
	 * Return model image url with replaced design, sku, color, widht and height
	 * @param  {String} url
	 * @param  {Object} opts { newDesign, newShortSku, newColor, newWidth, newHeight, newShade { anchorX, anchorY, res } }
	 * @return {String}
	 */
	function getUpdatedImageUrl (url, opts = {}) {

		let re;
		// replace shortSku
		if (opts.hasOwnProperty('newShortSku')) {

			re = /(.*\/ir\/render\/.*?\/)(.*?)(\?.*)/i;
			url = url.replace(re, `$1${opts.newShortSku}$3`);

		}

		// replace color
		if (opts.hasOwnProperty('newColor')) {

			re = /(.*?)\?color=(.*?)&(.*)/i;
			url = url.replace(re, `$1?color=${opts.newColor}&$3`);

		}

		// replace design and set new width and height
		if (opts.hasOwnProperty('newDesign')) {

			re = /(.*?)&src=is\((.*)\)(.*)/i;
			url = url.replace(re, `$1&src=is(${opts.newDesign})$3`);

		}

		// replace shade's anchorX, anchorY, Resolution
		if (opts.hasOwnProperty('newShade')) {

			if (opts.newShade.hasOwnProperty('anchorX') && opts.newShade.hasOwnProperty('anchorY')) {

				re = /(.*?&src=is\(.*\).*?)&anchor=[0-9]*\.?[0-9]*,[0-9]*\.?[0-9]*(.*)/i;
				url = url.replace(re, `$1&anchor=${opts.newShade.anchorX},${opts.newShade.anchorY}$2`);

			}
			if (opts.newShade.hasOwnProperty('res')) {

				re = /(.*?&src=is\(.*\).*?)&res=[0-9]*\.?[0-9]*(.*)/i;
				url = url.replace(re, `$1&res=${opts.newShade.res}$2`);

			}

		}

		return setDimensions(url, opts.newWidth, opts.newHeight);

	}

	/**
	 * Returns model image url based on given customized product model
	 * @param  {Backbone.Model} model customized product model
	 * @param  {Object} opts { newDesign, newShortSku, newColor, newWidth, newHeight, newShade { anchorX, anchorY, res } }
	 * @return {String}
	 */
	function getNewImageUrlByModel (model, opts) {

		let newDesign;
		let layer0;
		let widthShade;
		let heightShade;
		let widthFactor;
		let heightFactor;
		const url = model.get('ImageUrl');
		const re = /(.*?)&src=is\((.*)\)(.*)/i;
		const match = url.match(re);

		// find out layer 0
		layer0 = _.find(match[2].split('layer'), (l) => l.substr(0, 3) == '=0&');

		layer0 = layer0.split('&size=')[1].split(',');
		widthShade = parseFloat(layer0[0], 10);
		heightShade = parseFloat(layer0[1], 10);
		widthFactor = widthShade / model.area.Width;
		heightFactor = heightShade / model.area.Height;

		// newDesign all elements
		newDesign = match[2].split('layer').slice(0, 2).join('layer');
		// remove tail '&' if one exists
		if (newDesign[newDesign.length - 1] == '&') {

			newDesign = newDesign.slice(0, -1);

		}
		model.elements.text.forEach((m) => {

			newDesign += `&${_buildTextUrlPart(m, widthFactor, heightFactor)}`;

		});

		model.elements.image.forEach((m) => {

			newDesign += `&${_buildImageUrlPart(m, widthFactor, heightFactor)}`;

		});

		model.elements.pattern.forEach((m) => {

			newDesign += `&${_buildDesignUrlPart(m, widthFactor, heightFactor)}`;

		});

		opts.newDesign = newDesign;
		return getUpdatedImageUrl(url, opts);

	}

	return { getNewImageUrlByModel, setDimensions, getUpdatedImageUrl };

})();
